import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Loader2, ShieldCheck, X } from "lucide-react";
import { api, apiError } from "../../lib/api";

const CopyBtn = ({ text, id }) => {
  const [ok, setOk] = useState(false);
  return <button data-testid={`${id}-copy`} onClick={() => navigator.clipboard?.writeText(text).then(() => { setOk(true); setTimeout(() => setOk(false), 1500); })} className="glass-btn flex items-center gap-1 px-2 py-1 text-[11px]">{ok ? <Check className="h-3 w-3 text-[#3ecf8e]" /> : <Copy className="h-3 w-3" />} Copy</button>;
};

// Owner → Microsoft Store certification login: creates/resets the comped reviewer account and shows the password once.
export const useReviewerLogin = () => {
  const [busy, setBusy] = useState(false);
  const [cred, setCred] = useState(null);
  const make = async () => {
    setBusy(true);
    try { setCred(await api.adminReviewer()); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const modal = cred && (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" data-testid="reviewer-login-dialog" onClick={() => setCred(null)}>
      <div className="glass-panel w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div><h3 className="text-[15px] font-semibold text-white">Microsoft Store reviewer login</h3><p className="mt-1 text-[12px] text-[#9aa0a8]">Paste both into Partner Center → Additional Testing Info → Credentials.</p></div>
          <button data-testid="reviewer-login-close" onClick={() => setCred(null)} className="rounded p-1 text-[#9aa0a8] hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
        {[["Username", cred.email, "reviewer-email"], ["Password", cred.password, "reviewer-password"]].map(([l, v, id]) => (
          <div key={id} className="mt-3 flex items-center gap-2 rounded-lg border border-white/10 bg-[#0c1017] px-3 py-2">
            <span className="w-20 text-[11px] uppercase tracking-wide text-[#6b7079]">{l}</span>
            <code data-testid={id} className="min-w-0 flex-1 truncate font-mono text-[13px] text-[#e8e9ec]">{v}</code>
            <CopyBtn text={v} id={id} />
          </div>
        ))}
        <p className="mt-3 text-[11px] text-[#6b7079]">Sign-in page → type the username → "Use a password instead" → password. Always active, no trial limits, scrape guard off.</p>
      </div>
    </div>
  );
  return { make, busy, modal };
};

export const ReviewerLoginButton = () => {
  const { make, busy, modal } = useReviewerLogin();
  return (
    <>
      <button data-testid="admin-reviewer-login" onClick={make} disabled={busy} title="Create or reset the Microsoft Store reviewer login (always-active account, no trial limits) and copy the password into Partner Center → Additional Testing Info → Credentials" className="admin-pill">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 text-[#3ecf8e]" />} Store reviewer login</button>
      {modal}
    </>
  );
};
